import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import React from "react";
import useAdminContext from "../../Admin/AdminContextProvider";
import useAlerts from "../AlertProvider";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  paginationSelectFocus: {
    "& .MuiSelect-select": {
      "&:focus-visible": {
        outline: "2px solid #383838",
        outlineOffset: "2px",
        borderRadius: "4px",
      },
    },
    "& .MuiSelect-select:focus": {
      outline: "2px solid #383838",
      outlineOffset: "2px",
      borderRadius: "4px",
    },
  },
}));

export default function PaginatedTableResults({
  tableColumns,
  getTableResultsFunction,
  inputSearchCriteria,
  onRowClick,
  isRowSelected,
  defaultRowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 25],
}) {
  const { addErrorAlert } = useAlerts();
  const [isLoading, setIsLoading] = React.useState(true);
  const [sortBy, setSortBy] = React.useState("createdDate");
  const [sortDirection, setSortDirection] = React.useState("desc");
  const [searchResults, setSearchResults] = React.useState();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const adminContext = useAdminContext();
  const classes = useStyles();

  React.useEffect(() => {
    setIsLoading(true);
    getTableResultsFunction({
      ...inputSearchCriteria,
      page,
      pageSize: rowsPerPage,
      sortDirection: sortDirection.toUpperCase(),
      orderBy: sortBy,
    })
      .then(setSearchResults)
      .catch(() => {
        addErrorAlert("Error loading data");
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inputSearchCriteria,
    page,
    rowsPerPage,
    sortDirection,
    sortBy,
    // Including the dataResyncFlag will refresh the data in any paginated table when the flag changes
    adminContext.dataResyncFlag,
  ]);

  function handleSort(sortByColumn) {
    setPage(0);
    if (sortByColumn === sortBy) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortDirection("desc");
      setSortBy(sortByColumn);
    }
  }

  return isLoading && !searchResults ? (
    <Skeleton />
  ) : searchResults?.totalElements > 0 ? (
    <Paper>
      <TableContainer style={{ maxHeight: 450, position: "relative" }}>
        {isLoading && (
          <div
            style={{
              position: "absolute",
              // height: "100%",
              // width: "100%",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10000,
              backgroundColor: "rgba(0, 0, 0, .25)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </div>
        )}
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {tableColumns.map((header, i) => (
                <TableCell key={i}>
                  {header.sortBy ? (
                    <TableSortLabel
                      active={sortBy === header.sortBy}
                      direction={sortDirection}
                      onClick={() => handleSort(header.sortBy)}
                    >
                      {header.headerName}
                    </TableSortLabel>
                  ) : (
                    header.headerName
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody style={{ position: "relative" }}>
            {searchResults.content.map((result, i) => (
              <TableRow
                hover
                key={i}
                style={{ cursor: onRowClick ? "pointer" : "inherit" }}
                onClick={() => {
                  // Run the onRowClickFunction prop if one was provided
                  if (onRowClick) {
                    onRowClick(result);
                  }
                }}
                selected={isRowSelected ? isRowSelected(result) : false}
              >
                {tableColumns.map((header, j) => (
                  <TableCell key={j}>{header.getField(result)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        className={classes.paginationSelectFocus}
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        rowsPerPage={rowsPerPage}
        page={page}
        count={searchResults.totalElements}
        onChangePage={(e, newPage) => setPage(newPage)}
        onChangeRowsPerPage={(e) => {
          setPage(0);
          setRowsPerPage(e.target.value);
        }}
      />
    </Paper>
  ) : (
    <p>No results found</p>
  );
}
